// Vuex module for the Action Items page: review_item listing/resolution, the candidate-advancement
// endpoint, the cross-position hiring-stage listing behind the Offer tab, and the position-FAQ-to-
// company move endpoint. Position FAQ CRUD (get/put/post/
// delete) is intentionally NOT wrapped here — it stays a direct getAuthorizedApi call in the page,
// mirroring the established FaqLibraryView.vue convention (no dedicated FAQ store module exists).
// Writing an offer is NOT here either: the Offer tab reuses candidate/putCandidateOffer so both surfaces share one write path.
import { type Module } from "vuex";

import {
  CandidateReplyCategory,
  type DeliveryFailureReason,
  type DefaultApi as Api,
  type HiringStage,
  type HiringStageListItemResponse,
  type PositionFaqMoveRequest,
  type ReviewItemCategory,
  ReviewItemCategory as Category,
  type ReviewItemResolution,
  type ReviewItemResponse,
} from "@/api";
import type { State as RootState } from "@/stores";

export interface State {}

// The review_item categories that back the Action Items tabs (see backend plan §4). This is
// a strict subset of all review_item categories: CALL_UNSUCCESSFUL and
// RESUME_NOT_READY_TO_INVITE are explicitly out of scope (plan §8-1/§8-2), and UNANSWERED_FAQ is
// not a review_item concern at all for this page (the FAQ tab reads position_faqs directly, plan
// §2.3).
export const TAB_CATEGORIES: ReviewItemCategory[] = [
  Category.CandidateAdvancement,
  Category.InvitationSmsDeliveryFailed,
  Category.WrongSmsRecipient,
  Category.RecruiterFollowUpRequested,
  Category.CandidateSmsReply,
  Category.ArchivedApplicationCallback,
];

// Fetch every open review item across `categories`. The open-items listing is unpaginated: at the
// volumes this page deals with it is a single request returning the full array (backend plan §1.6).
export const fetchAllOpenReviewItems = async (
  api: Api,
  categories: ReviewItemCategory[],
): Promise<ReviewItemResponse[]> => {
  const response = await api.getReviewItems(categories);
  return response.data;
};

// One left-card: every open review_item for the same (category, candidate) folded into a single
// card (plan §3.1 "aggregate by candidate"). Display fields come from the newest item (items arrive
// newest-first, order by created_at desc, id desc); resolving/dismissing the card must PATCH every
// id in `itemIds`. `cardId` is a stable per-tab-category numeric id for the left-card list/selection
// machinery: the candidate id when present (unique per candidate within one category), or the
// negative of the sole underlying item's id when candidate_id is null (a non-admin never sees
// these rows per the DTO contract, so collisions cannot occur within a tab).
export interface AggregatedReviewItemCard {
  cardId: number;
  itemIds: number[];
  category: ReviewItemCategory;
  candidateId: number | null;
  positionId: number | null;
  firstName: string | null;
  lastName: string | null;
  phoneNumber: string | null;
  positionTitle: string | null;
  positionLocation: string | null;
  companyName: string;
  candidateArchived: boolean;
  createdAt: string;
  // True when any open item folded into this card could not be classified — checked across every
  // item, not just the newest, so an older unclassified reply is never hidden by a newer classified one.
  hasUnclassifiedReply: boolean;
  description: string | null;
  deliveryFailureReason: DeliveryFailureReason | null;
  title: string;
}

export const aggregateReviewItemsByCandidate = (
  items: ReviewItemResponse[],
): AggregatedReviewItemCard[] => {
  const byKey = new Map<string, AggregatedReviewItemCard>();
  for (const item of items) {
    const cardId = item.candidate_id ?? -item.id;
    const key = `${item.category}:${cardId}`;
    const isUnclassified =
      item.candidate_reply_category === CandidateReplyCategory.Unclassified;
    const existing = byKey.get(key);
    if (existing) {
      existing.itemIds.push(item.id);
      existing.hasUnclassifiedReply ||= isUnclassified;
      continue;
    }
    byKey.set(key, {
      cardId,
      itemIds: [item.id],
      category: item.category,
      candidateId: item.candidate_id,
      positionId: item.position_id,
      firstName: item.first_name,
      lastName: item.last_name,
      phoneNumber: item.phone_number,
      positionTitle: item.position_title,
      positionLocation: item.position_location,
      companyName: item.company_name,
      candidateArchived: item.candidate_archived,
      createdAt: item.created_at,
      hasUnclassifiedReply: isUnclassified,
      description: item.description,
      deliveryFailureReason: item.delivery_failure_reason,
      title: item.title,
    });
  }
  return Array.from(byKey.values());
};

// Split a flat item list by category into one bucket per tab category. Categories not present in
// `TAB_CATEGORIES` are dropped (defensive; the server is asked to filter already).
export const distributeReviewItemsByCategory = (
  items: ReviewItemResponse[],
): Record<string, ReviewItemResponse[]> => {
  const buckets: Record<string, ReviewItemResponse[]> = {};
  for (const category of TAB_CATEGORIES) buckets[category] = [];
  for (const item of items) {
    if (buckets[item.category]) buckets[item.category].push(item);
  }
  return buckets;
};

// Run `fn` over `ids` strictly in order, stopping at the first failure so the caller can report
// "N of M done" and keep whatever didn't run yet selected (plan §3.1-(2)/§7: partial-failure
// contract shared by bulk resolve/dismiss and bulk advance).
export interface SequentialRunResult<T> {
  succeeded: T[];
  failedAt: T | null;
  error: unknown;
}
export const runSequentialUntilFailure = async <T>(
  ids: T[],
  fn: (id: T) => Promise<void>,
): Promise<SequentialRunResult<T>> => {
  const succeeded: T[] = [];
  for (const id of ids) {
    try {
      await fn(id);
      succeeded.push(id);
    } catch (error) {
      return { succeeded, failedAt: id, error };
    }
  }
  return { succeeded, failedAt: null, error: null };
};

export const module: Module<State, RootState> = {
  namespaced: true,
  state: {},
  actions: {
    // categories defaults to the four tab categories server-side when omitted; callers pass an
    // explicit list so the loop's cursor state stays scoped to exactly what's being displayed.
    // this.dispatch (not a destructured context arg) matches the established store convention
    // (candidate.ts, position.ts, short_message.ts).
    async fetchReviewItems(
      {},
      categories: ReviewItemCategory[],
    ): Promise<ReviewItemResponse[]> {
      const api = (await this.dispatch("getAuthorizedApi", null, {
        root: true,
      })) as Api;
      return fetchAllOpenReviewItems(api, categories);
    },
    // Paginated (unlike the review-item listing); the page takes only the first page — a stage tab is a working list, not a full archive.
    async fetchHiringStages(
      {},
      {
        stages,
        pending,
        limit,
      }: { stages: HiringStage[]; pending?: boolean; limit?: number },
    ): Promise<HiringStageListItemResponse[]> {
      const api = (await this.dispatch("getAuthorizedApi", null, {
        root: true,
      })) as Api;
      const response = await api.getHiringStages(stages, limit, pending);
      return response.data;
    },
    async patchReviewItem(
      {},
      {
        reviewItemId,
        resolution,
      }: { reviewItemId: number; resolution: ReviewItemResolution | null },
    ): Promise<ReviewItemResponse> {
      const api = (await this.dispatch("getAuthorizedApi", null, {
        root: true,
      })) as Api;
      const response = await api.patchReviewItem(reviewItemId, {
        resolution_status: resolution,
      });
      return response.data;
    },
    async postCandidateAdvancement(
      {},
      { positionId, candidateId }: { positionId: number; candidateId: number },
    ): Promise<ReviewItemResponse> {
      const api = (await this.dispatch("getAuthorizedApi", null, {
        root: true,
      })) as Api;
      const response = await api.postCandidateAdvancement(
        positionId,
        candidateId,
      );
      return response.data;
    },
    async movePositionFaqToCompany(
      {},
      {
        positionId,
        faqId,
        body,
      }: {
        positionId: number;
        faqId: number;
        body: PositionFaqMoveRequest;
      },
    ) {
      const api = (await this.dispatch("getAuthorizedApi", null, {
        root: true,
      })) as Api;
      const response = await api.movePositionFaqToCompany(
        faqId,
        positionId,
        body,
      );
      return response.data;
    },
  },
};
